/**
 * Shared proctoring helpers for risk score, violation types, and labels.
 * Aligns with server ProctoringSession risk calculation (base 5 + type bonus).
 */

export const ALL_VIOLATION_TYPES = [
  'fullscreen_exit',
  'tab_switch',
  'copy_paste',
  'mouse_leave',
  'ai_assistance',
  'multiple_screen',
  'ip_change',
];

/** Legacy assessment proctoring (ProctoringSession violation totals). */
export function getRiskBadgeFromScore(score) {
  if (score === undefined || score === null || Number.isNaN(Number(score))) {
    return { label: 'No data', classes: 'bg-gray-100 text-gray-800' };
  }
  const n = Number(score);
  if (n < 50) return { label: 'Compliant', classes: 'bg-green-100 text-green-800' };
  if (n < 100) return { label: 'Flagged', classes: 'bg-orange-100 text-orange-800' };
  return { label: 'High Risk', classes: 'bg-red-100 text-red-800' };
}

function tierLegacyAssessment(score) {
  if (score === undefined || score === null || Number.isNaN(Number(score))) return -1;
  const n = Number(score);
  if (n < 50) return 0;
  if (n < 100) return 1;
  return 2;
}

/** AI interview proctoring (computeProctoringRiskScore) scale used for screening badges. */
function tierAiInterview(score) {
  if (score === undefined || score === null || Number.isNaN(Number(score))) return -1;
  const n = Number(score);
  if (n < 50) return 0;
  if (n < 100) return 1;
  return 2;
}

function badgeFromTier(tier) {
  if (tier <= -1) return { label: 'No data', classes: 'bg-gray-100 text-gray-800' };
  if (tier === 0) return { label: 'Compliant', classes: 'bg-green-100 text-green-800' };
  if (tier === 1) return { label: 'Flagged', classes: 'bg-orange-100 text-orange-800' };
  return { label: 'High Risk', classes: 'bg-red-100 text-red-800' };
}

export function getInterviewProctoringRiskBadgeFromScore(score) {
  return badgeFromTier(tierAiInterview(score));
}

/**
 * Screening row: interview and assessment proctoring use different score scales.
 * Badge reflects the worse tier of the two.
 */
export function getCombinedScreeningProctoringBadge(interviewScore, assessmentScore) {
  const t = Math.max(tierAiInterview(interviewScore), tierLegacyAssessment(assessmentScore));
  return badgeFromTier(t);
}

export function getViolationTypePoints(type, count) {
  const n = Number(count) || 0;
  const base = n * 5;
  const bonus =
    type === 'ai_assistance'
      ? n * 15
      : type === 'multiple_screen'
        ? n * 12
        : type === 'copy_paste'
          ? n * 8
          : 0;
  return base + bonus;
}

function getPointsPerOccurrence(type) {
  return type === 'ai_assistance'
    ? 20
    : type === 'multiple_screen'
      ? 17
      : type === 'copy_paste'
        ? 13
        : 5;
}

export function formatViolationTypeLabel(type) {
  return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

export function getViolationTypeDescription(type) {
  const pts = getPointsPerOccurrence(type);
  const desc =
    type === 'ai_assistance'
      ? 'AI assistance detected'
      : type === 'multiple_screen'
        ? 'Multiple screens detected'
        : type === 'copy_paste'
          ? 'Copy/paste activity'
          : type === 'fullscreen_exit'
            ? 'Exited fullscreen'
            : type === 'tab_switch'
              ? 'Switched tabs'
              : type === 'mouse_leave'
                ? 'Mouse left window'
                : type === 'ip_change'
                  ? 'IP address changed'
                  : 'Other';
  return { pts, desc };
}

export function getTotalRiskPointsFromDetails(details) {
  if (!details) return 0;
  const byType = details.violations?.byType || {};
  const entries = Object.entries(byType);
  if (entries.length === 0) return 0;
  return entries.reduce((sum, [type, count]) => sum + getViolationTypePoints(type, count), 0);
}

export function getDisplayRiskScore(details) {
  return getTotalRiskPointsFromDetails(details);
}
